// Feature Flags Service voor het beheren van beta features
// Beta features zijn standaard uitgeschakeld op productie en kunnen via console commands worden geactiveerd

interface FeatureFlags {
    supportingDocuments: boolean;
    additionalRecordings: boolean;
    tagsManager: boolean;
    searchRecordings: boolean;
    // Voeg hier nieuwe beta features toe
}

class FeatureFlagsService {
    private static instance: FeatureFlagsService;
    private flags: FeatureFlags;
    private readonly STORAGE_KEY = 'notizy_feature_flags';

    private constructor() {
        this.flags = this.getDefaultFlags();
        this.loadFromStorage();
        this.setupConsoleCommands();
    }

    public static getInstance(): FeatureFlagsService {
        if (!FeatureFlagsService.instance) {
            FeatureFlagsService.instance = new FeatureFlagsService();
        }
        return FeatureFlagsService.instance;
    }

    // Bepaal default flags op basis van domein
    private getDefaultFlags(): FeatureFlags {
        const hostname = window.location.hostname;
        const isProduction = hostname === 'web.notizy.nl' || hostname.includes('notizy.nl');

        // Op productie zijn beta features standaard uit
        // Op development/localhost zijn ze standaard aan
        const defaultValue = !isProduction;

        return {
            supportingDocuments: true,
            additionalRecordings: true,
            tagsManager: true,
            searchRecordings: defaultValue
        };
    }

    // Laad opgeslagen flags uit localStorage
    private loadFromStorage(): void {
        try {
            const stored = localStorage.getItem(this.STORAGE_KEY);
            if (stored) {
                const parsedFlags = JSON.parse(stored) as Partial<FeatureFlags>;
                this.flags = { ...this.flags, ...parsedFlags };
            }
        } catch (error) {
            console.warn('Kon feature flags niet laden uit localStorage:', error);
        }
    }

    // Sla flags op in localStorage
    private saveToStorage(): void {
        try {
            localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.flags));
        } catch (error) {
            console.warn('Kon feature flags niet opslaan:', error);
        }
    }

    // Setup console commands voor feature activatie
    private setupConsoleCommands(): void {
        // Maak console commands beschikbaar op window object
        (window as any).notizyFeatures = {
            // Toon huidige status van alle features
            status: () => {
                console.log('🚀 Notizy Feature Flags Status:');
                console.table(this.flags);
                console.log('💡 Gebruik notizyFeatures.enable("featureName") of notizyFeatures.disable("featureName")');
                console.log('🔄 Gebruik notizyFeatures.reset() om terug te gaan naar defaults');
            },

            // Activeer een feature
            enable: (featureName: keyof FeatureFlags) => {
                if (featureName in this.flags) {
                    this.flags[featureName] = true;
                    this.saveToStorage();
                    console.log(`✅ Feature "${featureName}" geactiveerd. Herlaad de pagina om de wijzigingen te zien.`);
                    this.notifyListeners();
                } else {
                    console.error(`❌ Onbekende feature: "${featureName}". Beschikbare features:`, Object.keys(this.flags));
                }
            },

            // Deactiveer een feature
            disable: (featureName: keyof FeatureFlags) => {
                if (featureName in this.flags) {
                    this.flags[featureName] = false;
                    this.saveToStorage();
                    console.log(`🚫 Feature "${featureName}" gedeactiveerd. Herlaad de pagina om de wijzigingen te zien.`);
                    this.notifyListeners();
                } else {
                    console.error(`❌ Onbekende feature: "${featureName}". Beschikbare features:`, Object.keys(this.flags));
                }
            },

            // Reset naar default waarden
            reset: () => {
                this.flags = this.getDefaultFlags();
                this.saveToStorage();
                console.log('🔄 Feature flags gereset naar default waarden. Herlaad de pagina om de wijzigingen te zien.');
                this.notifyListeners();
            },

            // Toon help
            help: () => {
                console.log(`
🚀 Notizy Feature Flags Console Commands:

📊 notizyFeatures.status()           - Toon huidige status van alle features
✅ notizyFeatures.enable("feature")   - Activeer een feature
🚫 notizyFeatures.disable("feature") - Deactiveer een feature  
🔄 notizyFeatures.reset()            - Reset naar default waarden
❓ notizyFeatures.help()             - Toon deze help

📝 Beschikbare features:
${Object.keys(this.flags).map(key => `   • ${key}`).join('\n')}

💡 Tip: Op productie zijn beta features standaard uitgeschakeld.
`);
            }
        };

        // Toon een welkomstbericht in development
        if (process.env.NODE_ENV === 'development') {
            console.log('🚀 Notizy Feature Flags geladen! Typ notizyFeatures.help() voor commands.');
        }
    }

    // Listeners voor real-time updates (voor toekomstig gebruik)
    private listeners: Array<() => void> = [];

    private notifyListeners(): void {
        this.listeners.forEach(listener => listener());
    }

    public addListener(listener: () => void): void {
        this.listeners.push(listener);
    }

    public removeListener(listener: () => void): void {
        this.listeners = this.listeners.filter(l => l !== listener);
    }

    // Public API voor componenten
    public isEnabled(feature: keyof FeatureFlags): boolean {
        return this.flags[feature] || false;
    }

    public getFlags(): Readonly<FeatureFlags> {
        return { ...this.flags };
    }

    // Debug info
    public getDebugInfo(): { hostname: string; isProduction: boolean; flags: FeatureFlags } {
        const hostname = window.location.hostname;
        const isProduction = hostname === 'web.notizy.nl' || hostname.includes('notizy.nl');

        return {
            hostname,
            isProduction,
            flags: this.flags
        };
    }
}

// Export singleton instance
export const featureFlags = FeatureFlagsService.getInstance();

// Export types voor TypeScript
export type { FeatureFlags };
export default FeatureFlagsService;
